import { Layout } from '../components/Layout';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Check, Send, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function Transfers({ user, onLogout }) {
  const [transfers, setTransfers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    from_location: '',
    to_location: '',
    items: [{ product_id: '', quantity: '', unit: 'pcs' }]
  });

  useEffect(() => {
    fetchTransfers();
    fetchLocations();
    fetchProducts();
  }, []);

  const fetchTransfers = async () => {
    try {
      const res = await axios.get(`${API}/transfers`, getAuthHeader());
      setTransfers(res.data);
    } catch (error) {
      console.error('Failed to fetch transfers:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`, getAuthHeader());
      setLocations(res.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, getAuthHeader());
      setProducts(res.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const transferData = {
      request_id: `req_${Date.now()}`,
      from_location: formData.from_location,
      to_location: formData.to_location,
      status: 'pending',
      requested_items: formData.items,
      requested_by: user.phone,
    };

    try {
      await axios.post(`${API}/transfers/request`, transferData, getAuthHeader());
      toast.success('Transfer request created');
      setShowForm(false);
      resetForm();
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transfer');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await axios.put(`${API}/transfers/${requestId}/approve`, {}, getAuthHeader());
      toast.success('Transfer approved');
      fetchTransfers();
    } catch (error) {
      toast.error('Failed to approve transfer');
    }
  };

  const handleDispatch = async (requestId) => {
    try {
      await axios.put(`${API}/transfers/${requestId}/dispatch`, {}, getAuthHeader());
      toast.success('Transfer dispatched');
      fetchTransfers();
    } catch (error) {
      toast.error('Failed to dispatch transfer');
    }
  };

  const handleReceive = async (requestId) => {
    try {
      await axios.put(`${API}/transfers/${requestId}/receive`, {}, getAuthHeader());
      toast.success('Transfer received');
      fetchTransfers();
    } catch (error) {
      toast.error('Failed to receive transfer');
    }
  };

  const resetForm = () => {
    setFormData({
      from_location: '',
      to_location: '',
      items: [{ product_id: '', quantity: '', unit: 'pcs' }]
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', quantity: '', unit: 'pcs' }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-functional-warning/10 text-functional-warning',
      approved: 'bg-functional-info/10 text-functional-info',
      dispatched: 'bg-secondary/10 text-secondary',
      received: 'bg-functional-success/10 text-functional-success',
      rejected: 'bg-functional-error/10 text-functional-error',
    };
    return colors[status] || '';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold text-stone-900">Transfers</h1>
            <p className="text-stone-600 mt-1">Manage stock transfers between locations</p>
          </div>
          <Button
            data-testid="create-transfer-button"
            onClick={() => setShowForm(!showForm)}
            className="bg-secondary hover:bg-secondary-hover text-white"
          >
            <Plus size={20} className="mr-2" />
            New Transfer
          </Button>
        </div>

        {showForm && (
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6 mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-800 mb-6">New Transfer Request</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">From Location</label>
                  <Select value={formData.from_location} onValueChange={(value) => setFormData({ ...formData, from_location: value })}>
                    <SelectTrigger data-testid="from-location-select">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(loc => (
                        <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">To Location</label>
                  <Select value={formData.to_location} onValueChange={(value) => setFormData({ ...formData, to_location: value })}>
                    <SelectTrigger data-testid="to-location-select">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(loc => (
                        <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-stone-700 mb-3">Items</label>
                {formData.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-4 mb-3">
                    <Select value={item.product_id} onValueChange={(value) => updateItem(idx, 'product_id', value)}>
                      <SelectTrigger data-testid={`product-select-${idx}`}>
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map(p => (
                          <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <input
                      type="number"
                      data-testid={`quantity-input-${idx}`}
                      placeholder="Quantity"
                      value={item.quantity}
                      onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                      className="h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                    <input
                      type="text"
                      data-testid={`unit-input-${idx}`}
                      placeholder="Unit"
                      value={item.unit}
                      onChange={(e) => updateItem(idx, 'unit', e.target.value)}
                      className="h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                ))}
                <Button type="button" onClick={addItem} variant="outline" size="sm" className="mt-2">
                  <Plus size={16} className="mr-2" />
                  Add Item
                </Button>
              </div>

              <div className="flex gap-3">
                <Button type="submit" data-testid="submit-transfer-button" className="bg-primary hover:bg-primary-hover text-white">
                  Create Transfer Request
                </Button>
                <Button type="button" onClick={() => setShowForm(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-sm shadow-sm">
          <div className="p-6 border-b border-stone-200">
            <h2 className="font-heading text-2xl font-semibold text-stone-800">Transfer Requests</h2>
          </div>
          <div className="divide-y divide-stone-100">
            {transfers.map((transfer) => (
              <div key={transfer.request_id} className="p-6 hover:bg-stone-50">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="font-mono text-sm text-stone-500 mb-1">{transfer.request_id}</div>
                    <div className="font-semibold text-stone-900">
                      {transfer.from_location} → {transfer.to_location}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-sm text-sm font-medium ${getStatusColor(transfer.status)}`}>
                    {transfer.status}
                  </span>
                </div>
                <div className="mb-4">
                  <div className="text-sm text-stone-600">Items: {transfer.requested_items?.length || 0}</div>
                </div>
                <div className="flex gap-2">
                  {transfer.status === 'pending' && user.role === 'admin' && (
                    <Button
                      size="sm"
                      data-testid={`approve-${transfer.request_id}`}
                      onClick={() => handleApprove(transfer.request_id)}
                      className="bg-functional-info hover:bg-functional-info/90 text-white"
                    >
                      <Check size={16} className="mr-1" />
                      Approve
                    </Button>
                  )}
                  {transfer.status === 'approved' && user.role === 'central_kitchen' && (
                    <Button
                      size="sm"
                      data-testid={`dispatch-${transfer.request_id}`}
                      onClick={() => handleDispatch(transfer.request_id)}
                      className="bg-secondary hover:bg-secondary-hover text-white"
                    >
                      <Send size={16} className="mr-1" />
                      Dispatch
                    </Button>
                  )}
                  {transfer.status === 'dispatched' && user.role === 'outlet_staff' && (
                    <Button
                      size="sm"
                      data-testid={`receive-${transfer.request_id}`}
                      onClick={() => handleReceive(transfer.request_id)}
                      className="bg-functional-success hover:bg-functional-success/90 text-white"
                    >
                      <Package size={16} className="mr-1" />
                      Receive
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {transfers.length === 0 && (
              <div className="text-center py-12 text-stone-400">No transfer requests found</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
